/**
 * What the ad platforms are told, and when.
 *
 * Two tags — Google (GA4 and Google Ads share one script) and Meta's pixel —
 * and four things worth telling them: a page was viewed, an account was
 * created, a checkout was started, money arrived. Nothing else. A campaign is
 * judged on sign-ups per dollar and subscriptions per sign-up, and both are
 * only measurable if those two events reach the platform that spent the
 * dollar.
 *
 * Which tags load is decided by the server (`/api/auth/config`), not at build
 * time, so a deployment without a campaign ships no third-party script and a
 * tag can be switched off from the environment. With every id empty this
 * module does nothing at all.
 *
 * The vendor calls are *planned* by pure functions and *dispatched* by two
 * thin ones, so what each event turns into can be tested without a browser.
 */

import type { AnalyticsConfig } from "../api/types";

export type AnalyticsEvent =
  | { name: "page_view"; path: string }
  | { name: "sign_up"; method: "password" | "google" }
  | { name: "begin_checkout"; tier: string }
  | {
      name: "purchase";
      kind: "subscription" | "report";
      /** The plan for a subscription; absent for a report. */
      tier?: string;
      /** In cents, as the server states it. */
      amountCents: number;
      /** Stripe's checkout session id — what makes a reload not a second sale. */
      transactionId: string;
    };

/** One call to a vendor's global: `gtag(...args)` or `fbq(...args)`. */
export interface VendorCall {
  vendor: "gtag" | "fbq";
  args: unknown[];
}

const CURRENCY = "USD";

export function anyTagConfigured(config: AnalyticsConfig | null): boolean {
  if (!config) return false;
  return Boolean(
    config.ga_measurement_id || config.google_ads_id || config.meta_pixel_id,
  );
}

/**
 * Whether the visitor has asked not to be measured.
 *
 * Global Privacy Control is a browser signal — a setting, or an extension
 * that sets it — and the privacy policy (§2a) promises that when it is sent,
 * no tag loads and no campaign parameter is kept. This is where that promise
 * is kept. `navigator.globalPrivacyControl` is the standard's name for it;
 * absent means not sent, which is the common case.
 */
export function optedOut(
  nav: { globalPrivacyControl?: boolean } | undefined = typeof navigator ===
  "undefined"
    ? undefined
    : (navigator as Navigator & { globalPrivacyControl?: boolean }),
): boolean {
  return Boolean(nav?.globalPrivacyControl);
}

/**
 * Whether this page load measures anything at all: tags configured *and*
 * the visitor has not refused. One answer for both the tags and the
 * attribution record, because the policy makes them one promise.
 */
export function measurementAllowed(config: AnalyticsConfig | null): boolean {
  return anyTagConfigured(config) && !optedOut();
}

/** Whether Google's script is wanted at all. */
export function wantsGoogle(config: AnalyticsConfig): boolean {
  return Boolean(config.ga_measurement_id || config.google_ads_id);
}

/** The id Google's script is loaded under — either product's will do. */
export function googleScriptId(config: AnalyticsConfig): string {
  return config.ga_measurement_id || config.google_ads_id;
}

/** The calls that set the tags up, before any event. */
export function planSetup(config: AnalyticsConfig): VendorCall[] {
  const calls: VendorCall[] = [];
  if (wantsGoogle(config)) {
    calls.push({ vendor: "gtag", args: ["js", new Date()] });
    if (config.ga_measurement_id) {
      // Page views are sent by hand on route change — see planEvent — so the
      // automatic one on load would double-count the landing page.
      calls.push({
        vendor: "gtag",
        args: ["config", config.ga_measurement_id, { send_page_view: false }],
      });
    }
    if (config.google_ads_id) {
      calls.push({ vendor: "gtag", args: ["config", config.google_ads_id] });
    }
  }
  if (config.meta_pixel_id) {
    calls.push({ vendor: "fbq", args: ["init", config.meta_pixel_id] });
  }
  return calls;
}

/** What one event turns into, for whichever tags are configured. */
export function planEvent(
  event: AnalyticsEvent,
  config: AnalyticsConfig,
): VendorCall[] {
  const calls: VendorCall[] = [];
  const ga = Boolean(config.ga_measurement_id);
  const meta = Boolean(config.meta_pixel_id);
  const ads = config.google_ads_id;

  switch (event.name) {
    case "page_view": {
      if (ga) {
        calls.push({
          vendor: "gtag",
          args: ["event", "page_view", { page_path: event.path }],
        });
      }
      if (meta) calls.push({ vendor: "fbq", args: ["track", "PageView"] });
      return calls;
    }
    case "sign_up": {
      if (ga) {
        calls.push({
          vendor: "gtag",
          args: ["event", "sign_up", { method: event.method }],
        });
      }
      if (ads && config.google_ads_signup_label) {
        calls.push({
          vendor: "gtag",
          args: [
            "event",
            "conversion",
            { send_to: `${ads}/${config.google_ads_signup_label}` },
          ],
        });
      }
      if (meta) {
        calls.push({ vendor: "fbq", args: ["track", "CompleteRegistration"] });
      }
      return calls;
    }
    case "begin_checkout": {
      if (ga) {
        calls.push({
          vendor: "gtag",
          args: [
            "event",
            "begin_checkout",
            { items: [{ item_id: event.tier, item_name: planName(event.tier) }] },
          ],
        });
      }
      if (meta) {
        calls.push({
          vendor: "fbq",
          args: ["track", "InitiateCheckout", { content_name: event.tier }],
        });
      }
      return calls;
    }
    case "purchase": {
      const value = event.amountCents / 100;
      const itemId = event.tier ?? "report";
      if (ga) {
        calls.push({
          vendor: "gtag",
          args: [
            "event",
            "purchase",
            {
              transaction_id: event.transactionId,
              value,
              currency: CURRENCY,
              items: [
                {
                  item_id: itemId,
                  item_name: event.kind === "report" ? "Report" : planName(itemId),
                  item_category: event.kind,
                  price: value,
                  quantity: 1,
                },
              ],
            },
          ],
        });
      }
      if (ads && config.google_ads_purchase_label) {
        calls.push({
          vendor: "gtag",
          args: [
            "event",
            "conversion",
            {
              send_to: `${ads}/${config.google_ads_purchase_label}`,
              value,
              currency: CURRENCY,
              transaction_id: event.transactionId,
            },
          ],
        });
      }
      if (meta) {
        calls.push({
          vendor: "fbq",
          args: [
            "track",
            "Purchase",
            { value, currency: CURRENCY, content_name: itemId },
            { eventID: event.transactionId },
          ],
        });
      }
      return calls;
    }
  }
  // Every case above returns; this line exists so an event added to the
  // union without a case is a type error rather than a silent nothing.
  return calls;
}

function planName(tier: string): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

// ---------------------------------------------------------------------------
// Dedupe
// ---------------------------------------------------------------------------

const RECORDED_PREFIX = "ds.recorded.";

/**
 * Whether this transaction has been reported already. Stripe sends the buyer
 * back to a URL that names the session; a reload, a Back, or a bookmark of
 * that URL would report the sale again. First call answers false and marks
 * it; every later call answers true. Storage failing reads as "not yet",
 * which over-reports rather than under-reports — GA4 dedupes on the
 * transaction id anyway.
 */
export function alreadyRecorded(
  transactionId: string,
  storage: Pick<Storage, "getItem" | "setItem"> | null = safeStorage(),
): boolean {
  if (!transactionId || !storage) return false;
  const key = RECORDED_PREFIX + transactionId;
  try {
    if (storage.getItem(key)) return true;
    storage.setItem(key, "1");
  } catch {
    return false;
  }
  return false;
}

function safeStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// The browser
// ---------------------------------------------------------------------------

/** Meta's global: a function carrying the queue its script drains on load. */
interface MetaPixel {
  (...args: unknown[]): void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  callMethod?: (...args: unknown[]) => void;
  push?: unknown;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: MetaPixel;
    _fbq?: unknown;
  }
}

let active: AnalyticsConfig | null = null;
/** Events fired before the config arrived, replayed once it does. */
const pending: AnalyticsEvent[] = [];

/**
 * Load whichever tags are configured. Idempotent, and a no-op for a config
 * with every id empty — which is the normal state of a deployment with no
 * campaign running.
 */
export function configureAnalytics(config: AnalyticsConfig): void {
  if (active || typeof window === "undefined") return;
  if (!measurementAllowed(config)) return;
  active = config;

  if (wantsGoogle(config)) loadGoogle(googleScriptId(config));
  if (config.meta_pixel_id) loadMeta();

  for (const call of planSetup(config)) dispatch(call);
  // The landing page itself, now that a tag exists to receive it.
  dispatch({ vendor: "fbq", args: ["track", "PageView"] });
  if (config.ga_measurement_id) {
    dispatch({
      vendor: "gtag",
      args: ["event", "page_view", { page_path: currentPath() }],
    });
  }
  while (pending.length) track(pending.shift()!);
}

/** Report an event. Safe to call before configuration and with none. */
export function track(event: AnalyticsEvent): void {
  if (!active) {
    // Nothing is known yet about whether tags exist. Keep it: sign-ups that
    // happen in the first second of a session are exactly the ones an ad
    // brought.
    if (pending.length < 20) pending.push(event);
    return;
  }
  for (const call of planEvent(event, active)) dispatch(call);
}

function dispatch(call: VendorCall): void {
  try {
    const fn = call.vendor === "gtag" ? window.gtag : window.fbq;
    if (typeof fn === "function") fn(...call.args);
  } catch {
    // A tag throwing is the vendor's problem, never the page's.
  }
}

function currentPath(): string {
  return window.location.pathname + window.location.search;
}

function loadGoogle(id: string): void {
  window.dataLayer = window.dataLayer ?? [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // Google's own snippet pushes `arguments`, not an array — the script
      // reads it as such.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
}

function loadMeta(): void {
  if (window.fbq) return;
  // Meta's snippet, without the minification: a queue that the real script
  // drains once it loads.
  const queue: unknown[] = [];
  const fbq: MetaPixel = (...args: unknown[]) => {
    // Read back off window: the real script replaces `callMethod` on this
    // very object once it has loaded, and TypeScript cannot see that from
    // here.
    const live = window.fbq as MetaPixel | undefined;
    if (live?.callMethod) live.callMethod(...args);
    else queue.push(args);
  };
  fbq.queue = queue;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.push = fbq;
  window.fbq = fbq;
  window._fbq = fbq;
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);
}

/** For tests: forget the loaded config. */
export function resetAnalyticsForTests(): void {
  active = null;
  pending.length = 0;
}
